/**
 * Dynamic Arrow Positioning for Flickity Carousels
 * Efficiently measures actual dots width and positions arrows accordingly
 */

// Global state
let observers = new Map(); // Track observers for cleanup
let resizeTimeout = null;

function initArrowPositioning() {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupObservers);
  } else {
    setupObservers();
  }
}

function setupObservers() {
  // Find all flickity-controls-group carousels that are actually enabled
  const carousels = document.querySelectorAll('.flickity-wrapper.flickity-controls-group.flickity-enabled');
  
  // Also find compare table carousels (they don't use flickity-controls-group)
  const compareTableCarousels = document.querySelectorAll('.f-compare-table__wrapper .flickity-wrapper.flickity-enabled');
  
  // Combine both sets
  const allCarousels = [...carousels, ...compareTableCarousels];
  
  allCarousels.forEach(carousel => {
    if (!observers.has(carousel)) {
      updateArrowPosition(carousel);
      observeCarousel(carousel);
    }
  });
}

function observeCarousel(carousel) {
  const dotsContainer = carousel.querySelector('.flickity-page-dots');
  if (!dotsContainer) return;

  // Use ResizeObserver for efficient width monitoring
  if (window.ResizeObserver) {
    const resizeObserver = new ResizeObserver(() => {
      debouncedUpdate(carousel);
    });
    resizeObserver.observe(dotsContainer);
    
    // Store observer for cleanup
    observers.set(carousel, resizeObserver);
  }

  // Watch for Flickity events (only once per carousel)
  const flickityInstance = carousel.flickity;
  if (flickityInstance && !carousel._arrowPositioningInitialized) {
    flickityInstance.on('ready', () => {
      debouncedUpdate(carousel);
      // Recalculate height after a short delay to account for content settling
      setTimeout(() => {
        if (flickityInstance) {
          flickityInstance.resize();
        }
      }, 100);
    });
    carousel._arrowPositioningInitialized = true;
  }
}

function debouncedUpdate(carousel) {
  // Debounce rapid updates
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    updateArrowPosition(carousel);
  }, 16); // ~60fps
}

function updateArrowPosition(carousel) {
  const dotsContainer = carousel.querySelector('.flickity-page-dots');
  
  // If no dots container, use fallback spacing for arrows-only carousels
  if (!dotsContainer || dotsContainer.offsetWidth === 0) {
    // For carousels without dots, use a minimal width and extra spacing
    carousel.style.setProperty('--actual-pagination-width', '0rem');
    
    // Apply different spacing based on section type
    const isHandpicked = carousel.closest('.f-custom__block--product_list, [data-section-type="handpicked-products"], .handpicked-products');
    const isProductTabs = carousel.closest('.f-product-tabs__blocks');
    const isCollectionList = carousel.closest('.collection-list');
    
    if (isHandpicked || isProductTabs || isCollectionList) {
      carousel.style.setProperty('--extra-arrow-spacing', '2.5rem');
    } else {
      carousel.style.setProperty('--extra-arrow-spacing', '2rem');
    }
    return;
  }

  // Get the actual rendered width of the dots container
  const actualWidth = dotsContainer.offsetWidth;
  const computedStyles = window.getComputedStyle(dotsContainer);
  
  // Convert to rem (assuming 1rem = 16px)
  const actualWidthRem = actualWidth / 16;
  
  // Debug: Log the dots container details
  console.log('Dots container analysis:', {
    offsetWidth: actualWidth,
    widthRem: actualWidthRem,
    paddingLeft: computedStyles.paddingLeft,
    paddingRight: computedStyles.paddingRight,
    marginLeft: computedStyles.marginLeft,
    marginRight: computedStyles.marginRight,
    totalWidth: dotsContainer.getBoundingClientRect().width
  });
  
  // Update the CSS custom property with extra spacing
  carousel.style.setProperty('--actual-pagination-width', `${actualWidthRem}rem`);
  
  // Apply different spacing based on section type
  const isHandpicked = carousel.closest('.f-custom__block--product_list, [data-section-type="handpicked-products"], .handpicked-products');
  const isProductTabs = carousel.closest('.f-product-tabs__blocks');
  const isCollectionList = carousel.closest('.collection-list');
  
  if (isHandpicked || isProductTabs || isCollectionList) {
    carousel.style.setProperty('--extra-arrow-spacing', '1.5rem');
  } else {
    carousel.style.setProperty('--extra-arrow-spacing', '1rem');
  }
}

// Cleanup function for removed carousels
function cleanup() {
  observers.forEach((observer, carousel) => {
    if (!document.contains(carousel)) {
      observer.disconnect();
      observers.delete(carousel);
    }
  });
}

// Initialize when script loads
initArrowPositioning();

// Re-initialize when new content is loaded (for dynamic sections)
document.addEventListener('shopify:section:load', () => {
  setTimeout(initArrowPositioning, 100);
});

document.addEventListener('shopify:section:reorder', () => {
  setTimeout(initArrowPositioning, 100);
});

// Re-initialize when product tabs change
document.addEventListener('tabChange', () => {
  setTimeout(initArrowPositioning, 100);
});

// Also listen for tab content changes (when switching between tabs)
document.addEventListener('click', (event) => {
  // Check if clicked element is a tab button
  if (event.target.matches('.f-tabs__nav, [role="tab"]')) {
    setTimeout(() => {
      // Find the newly active tab content and recalculate arrows
      const activeTabContent = document.querySelector('.f-tabs__content:not([hidden])');
      if (activeTabContent) {
        const carousel = activeTabContent.querySelector('.flickity-wrapper.flickity-controls-group.flickity-enabled');
        if (carousel) {
          updateArrowPosition(carousel);
        }
      }
    }, 150); // Wait for tab transition to complete
  }
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  cleanup();
});
